/**
 * Selected-order detail panel for the Transfers workbench. Renders:
 *   - linked purchase order (Layer 4: the PO this transfer is waiting on)
 *   - discrepancy history (`public.transfer_order_discrepancy`)
 *   - receive events history (`public.transfer_receive_event`)
 *   - shipment rows linked to this transfer
 *
 * Lifecycle actions, packing, and labor live on the transfer list cards;
 * boxes and allocations are edited on the pre-pack transfer page.
 */

import { useCallback, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { apiFetch, ApiError } from "@/lib/api";
import { Badge } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import { Card, CardBody, CardHeader, CardTitle } from "@/components/ui/Card";
import { useHubToast } from "@/components/ui/HubToast";
import { SearchableSelect } from "@/components/ui/SearchableSelect";
import { MrpSkuWithUnitLabel } from "@/components/mrp/MrpSkuWithUnitLabel";

import { type TransferBoardOrder } from "./transfersWorkbenchTypes";
import type { MrpSkuHoverHintFields } from "@/lib/mrp";

export function TransferOrderDetailPanel({
  order,
  hubWritesAllowed,
  skuHoverHints,
}: {
  order: TransferBoardOrder;
  hubWritesAllowed: boolean;
  lookupOriginOnHand: unknown;
  originInventoryLoading: boolean;
  skuHoverHints?: Record<string, MrpSkuHoverHintFields>;
}): JSX.Element {
  const queryClient = useQueryClient();
  const { showToast } = useHubToast();
  const invalidateBoard = useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: ["manufacturing", "transfers-board"] });
  }, [queryClient]);

  // ---------- Layer-4 linked purchase order ----------
  const [poSearch, setPoSearch] = useState("");
  const poSearchQuery = useQuery({
    queryKey: ["manufacturing", "transfers", "po-search", poSearch],
    queryFn: () =>
      apiFetch<{ rows: { id: string; number: string | null; supplier_name: string | null }[] }>(
        `/api/manufacturing/transfers/po-search?q=${encodeURIComponent(poSearch)}`,
      ),
    enabled: hubWritesAllowed,
    staleTime: 30_000,
  });
  const linkPoMutation = useMutation<
    { id: number; purchase_order_id: number | null },
    ApiError,
    number | null
  >({
    mutationFn: (purchaseOrderId) =>
      apiFetch<{ id: number; purchase_order_id: number | null }>(
        `/api/manufacturing/transfers/${String(order.id)}/purchase-order`,
        { method: "PATCH", body: { purchase_order_id: purchaseOrderId } },
      ),
    onSuccess: (result) => {
      showToast({
        tone: "success",
        message:
          result.purchase_order_id == null
            ? "Purchase order link cleared."
            : "Purchase order linked — receiving will announce its arrival here.",
      });
      setPoSearch("");
      invalidateBoard();
    },
    onError: (error) => showToast({ message: error.message, tone: "error" }),
  });

  const hasReceiveHistory = order.receive_events.length > 0 || order.discrepancies.length > 0;
  /** The linker is the only way to attach a PO, so it shows even with no history yet. */
  const showLinkedPoCard = hubWritesAllowed || order.purchase_order_id != null;

  if (!hasReceiveHistory && order.shipments.length === 0 && !showLinkedPoCard) {
    return <></>;
  }

  return (
    <div className="space-y-3">
      {/* --- Linked purchase order (Layer 4) -------------------------------- */}
      {showLinkedPoCard ? (
        <Card>
          <CardBody className="flex flex-wrap items-center gap-2 py-2.5">
            <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">Linked PO</span>
            {order.purchase_order_id != null ? (
              <>
                <Badge tone={order.purchase_order_received ? "success" : "warning"}>
                  {order.purchase_order_number ?? `PO #${String(order.purchase_order_id)}`}
                  {order.purchase_order_received ? " received" : " awaited"}
                </Badge>
                <span className="text-xs text-slate-500">
                  {order.purchase_order_received
                    ? "Stock arrived at SGS — a receiving receipt was submitted for this PO."
                    : "Waiting for a receiving receipt on this PO."}
                </span>
              </>
            ) : (
              <span className="text-xs text-slate-500">
                None — link the PO this transfer is waiting on and receiving will announce its arrival.
              </span>
            )}
            {hubWritesAllowed ? (
              <span className="ml-auto flex items-center gap-2">
                <span className="w-56">
                  <SearchableSelect
                    value={order.purchase_order_id != null ? String(order.purchase_order_id) : ""}
                    selectedLabelOverride={
                      order.purchase_order_id != null
                        ? order.purchase_order_number ?? `PO #${String(order.purchase_order_id)}`
                        : null
                    }
                    onValueChange={(option) => {
                      if (option?.value) linkPoMutation.mutate(Number(option.value));
                    }}
                    options={(poSearchQuery.data?.rows ?? []).map((row) => ({
                      value: row.id,
                      label: `${row.number ?? `#${row.id}`}${row.supplier_name ? ` — ${row.supplier_name}` : ""}`,
                    }))}
                    searchValue={poSearch}
                    onSearchChange={setPoSearch}
                    isLoading={poSearchQuery.isLoading}
                    placeholder="Link a purchase order…"
                    emptyMessage="No matching POs"
                  />
                </span>
                {order.purchase_order_id != null ? (
                  <Button
                    type="button"
                    size="sm"
                    variant="ghost"
                    disabled={linkPoMutation.isPending}
                    onClick={() => linkPoMutation.mutate(null)}
                  >
                    Clear
                  </Button>
                ) : null}
              </span>
            ) : null}
          </CardBody>
        </Card>
      ) : null}

      {hasReceiveHistory ? (
        <Card>
          <CardHeader>
            <CardTitle>
              Receive history{" "}
              <span className="text-slate-500">
                ({order.receive_events.length} event{order.receive_events.length === 1 ? "" : "s"})
              </span>
            </CardTitle>
          </CardHeader>
          <CardBody className="space-y-3 overflow-x-auto">
            {order.receive_events.length === 0 ? (
              <p className="text-sm text-slate-600">No receive events yet.</p>
            ) : (
              <table className="min-w-full border-collapse text-sm">
                <thead className="bg-slate-50 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">
                  <tr>
                    <th className="border-b border-slate-200 px-2 py-2">Received at</th>
                    <th className="border-b border-slate-200 px-2 py-2">By</th>
                    <th className="border-b border-slate-200 px-2 py-2">Notes</th>
                  </tr>
                </thead>
                <tbody>
                  {order.receive_events.map((event) => (
                    <tr key={event.id} className="border-b border-slate-100 last:border-b-0">
                      <td className="px-2 py-2 tabular-nums text-slate-700">{event.received_at}</td>
                      <td className="px-2 py-2 text-slate-700">
                        {event.received_by ?? <span className="text-slate-400">—</span>}
                      </td>
                      <td className="px-2 py-2 text-slate-600">
                        {event.notes ?? <span className="text-slate-400">—</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
            {order.discrepancies.length > 0 ? (
              <table className="min-w-full border-collapse text-sm">
                <thead className="bg-slate-50 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">
                  <tr>
                    <th className="border-b border-slate-200 px-2 py-2">SKU</th>
                    <th className="border-b border-slate-200 px-2 py-2 text-right">Expected</th>
                    <th className="border-b border-slate-200 px-2 py-2 text-right">Received</th>
                    <th className="border-b border-slate-200 px-2 py-2 text-right">Return to origin</th>
                    <th className="border-b border-slate-200 px-2 py-2 text-right">Lost</th>
                    <th className="border-b border-slate-200 px-2 py-2 text-right">Remaining</th>
                    <th className="border-b border-slate-200 px-2 py-2">Notes</th>
                  </tr>
                </thead>
                <tbody>
                  {order.discrepancies.map((row) => (
                    <tr key={row.id} className="border-b border-slate-100 last:border-b-0">
                      <td className="px-2 py-2 font-mono text-xs text-slate-900">
                        <MrpSkuWithUnitLabel
                          sku={row.sku_id ?? "—"}
                          unit={skuHoverHints?.[(row.sku_id ?? "").trim()]?.mv_unit}
                        />
                      </td>
                      <td className="px-2 py-2 text-right tabular-nums">{row.expected_qty}</td>
                      <td className="px-2 py-2 text-right tabular-nums">{row.received_qty}</td>
                      <td className="px-2 py-2 text-right tabular-nums">{row.return_to_origin_qty ?? 0}</td>
                      <td className="px-2 py-2 text-right tabular-nums">{row.lost_qty ?? 0}</td>
                      <td className="px-2 py-2 text-right tabular-nums">{row.remaining_qty ?? "—"}</td>
                      <td className="px-2 py-2 text-slate-600">
                        {row.notes ?? <span className="text-slate-400">—</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : null}
          </CardBody>
        </Card>
      ) : null}

      {order.shipments.length > 0 ? (
        <Card>
          <CardHeader>
            <CardTitle>
              Shipments <span className="text-slate-500">({order.shipments.length})</span>
            </CardTitle>
          </CardHeader>
          <CardBody className="overflow-x-auto">
            <table className="min-w-full border-collapse text-sm">
              <thead className="bg-slate-50 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">
                <tr>
                  <th className="border-b border-slate-200 px-2 py-2">Carrier</th>
                  <th className="border-b border-slate-200 px-2 py-2">Tracking / shipment id</th>
                  <th className="border-b border-slate-200 px-2 py-2">Shipped at</th>
                  <th className="border-b border-slate-200 px-2 py-2">Delivered at</th>
                </tr>
              </thead>
              <tbody>
                {order.shipments.map((shipment) => (
                  <tr key={shipment.id} className="border-b border-slate-100 last:border-b-0">
                    <td className="px-2 py-2 text-slate-700">
                      {shipment.carrier_name ?? shipment.carrier}
                    </td>
                    <td className="px-2 py-2 font-mono text-xs text-slate-900">
                      {shipment.carrier_shipment_id ?? <span className="text-slate-400">—</span>}
                    </td>
                    <td className="px-2 py-2 tabular-nums text-slate-700">{shipment.shipped_at}</td>
                    <td className="px-2 py-2 tabular-nums text-slate-700">
                      {shipment.delivered_at ?? <span className="text-slate-400">—</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardBody>
        </Card>
      ) : null}
    </div>
  );
}
